const SP_MQ = "(max-width: 768px)";
const CSS_VARS = [
  "--kv-vh",
  "--sp-vv-h",
  "--sp-vv-w",
  "--sp-vv-offset-y",
  "--sp-vv-offset-x",
  "--sp-vv-bottom-gap",
  "--kv-bottom-inset",
];

const history = [];
let panel = null;
let statusEl = null;

const round = (n) => (Number.isFinite(n) ? Math.round(n) : null);

const measureUnit = (unit) => {
  const probe = document.createElement("div");
  probe.style.cssText =
    `position:fixed;top:0;height:100${unit};pointer-events:none;visibility:hidden`;
  document.body.append(probe);
  const h = probe.offsetHeight;
  probe.remove();
  return round(h || window.innerHeight);
};

const boxOf = (el) => {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return {
    w: round(r.width),
    h: round(r.height),
    top: round(r.top),
    left: round(r.left),
  };
};

const readCssVar = (name) => {
  const inline = document.documentElement.style.getPropertyValue(name).trim();
  if (inline) return inline;
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || null;
};

const collectSnapshot = (label = "manual") => {
  const vv = window.visualViewport;
  const kv = document.querySelector(".js-kv");
  const field = kv?.querySelector(".kv__field");
  const sticky = kv?.querySelector(".kv__sticky");
  const frame = document.querySelector(".page-frame");
  const fieldBox = boxOf(field);
  const aspect =
    fieldBox?.w && fieldBox?.h ? Number((fieldBox.w / fieldBox.h).toFixed(3)) : null;

  const cssVars = {};
  for (const key of CSS_VARS) cssVars[key] = readCssVar(key);

  const kvClasses = kv
    ? [...kv.classList].filter((c) => c.startsWith("is-kv-") || c === "is-wide-gated")
    : [];

  const svh = measureUnit("svh");
  const lvh = measureUnit("lvh");

  return {
    label,
    at: new Date().toISOString(),
    ua: navigator.userAgent,
    href: location.href,
    mqSp768: window.matchMedia(SP_MQ).matches,
    inner: { w: round(window.innerWidth), h: round(window.innerHeight) },
    outer: { w: round(window.outerWidth), h: round(window.outerHeight) },
    screen: { w: round(window.screen?.width), h: round(window.screen?.height) },
    dpr: window.devicePixelRatio || null,
    visualViewport: vv
      ? {
          w: round(vv.width),
          h: round(vv.height),
          offsetTop: round(vv.offsetTop),
          offsetLeft: round(vv.offsetLeft),
          scale: vv.scale ?? null,
        }
      : null,
    probes: {
      svh,
      lvh,
      clientH: round(document.documentElement.clientHeight),
    },
    cssVars,
    htmlClass: [...document.documentElement.classList],
    bodyClass: [...document.body.classList],
    dataset: {
      kvSpPrelockW: document.documentElement.dataset.kvSpPrelockW || null,
      kvSpPrelockH: document.documentElement.dataset.kvSpPrelockH || null,
      kvSpVhSettled: document.documentElement.dataset.kvSpVhSettled || null,
      kvSpVhSource: document.documentElement.dataset.kvSpVhSource || null,
      kvPage: document.documentElement.dataset.kvPage || null,
    },
    kv: {
      phases: kvClasses,
      openingAttr: kv?.dataset.kvOpening || null,
      field: fieldBox,
      fieldAspect: aspect,
      sticky: boxOf(sticky),
      pageFrame: boxOf(frame),
      notes:
        aspect == null
          ? "no field"
          : Math.abs(aspect - 1.6) < 0.08
            ? "fieldAspect ≈ 16:10"
            : `fieldAspect=${aspect}`,
    },
  };
};

const pushSnapshot = (label) => {
  const snap = collectSnapshot(label);
  history.push(snap);
  if (history.length > 40) history.shift();
  if (statusEl) {
    statusEl.textContent = `${label} · settled=${snap.dataset.kvSpVhSettled ?? "-"}/${snap.dataset.kvSpVhSource ?? "-"} · vv=${snap.visualViewport?.h ?? "-"} · svh=${snap.probes.svh ?? "-"} · --kv-vh=${snap.cssVars["--kv-vh"] ?? "-"} · field=${snap.kv.field?.w ?? "-"}x${snap.kv.field?.h ?? "-"}`;
  }
  console.info("[kvdebug]", label, snap);
  return snap;
};

const buildPayload = () => ({
  title: "inoac kv debug",
  capturedAt: new Date().toISOString(),
  boot: window.__kvDebugBoot || null,
  latest: history[history.length - 1] || collectSnapshot("latest"),
  history,
});

const formatForAi = () =>
  `# inoac kv debug\n\`\`\`json\n${JSON.stringify(buildPayload(), null, 2)}\n\`\`\`\n`;

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.cssText = "position:fixed;left:8px;right:8px;bottom:120px;z-index:99999;height:30vh;";
    document.body.append(ta);
    ta.focus();
    ta.select();
    let ok = false;
    try {
      ok = document.execCommand("copy");
    } catch {
      ok = false;
    }
    if (ok) ta.remove();
    return ok;
  }
};

const ensurePanel = () => {
  if (panel) return panel;
  panel = document.createElement("div");
  panel.id = "kv-debug-panel";
  panel.setAttribute("data-lenis-prevent", "");
  panel.style.cssText = [
    "position:fixed",
    "right:8px",
    "bottom:8px",
    "z-index:99999",
    "max-width:min(92vw,360px)",
    "padding:10px",
    "border:1px solid #004d8a",
    "border-radius:8px",
    "background:rgba(255,255,255,.94)",
    "color:#004d8a",
    "font:12px/1.4 -apple-system,BlinkMacSystemFont,sans-serif",
    "box-shadow:0 4px 16px rgba(0,0,0,.18)",
    "pointer-events:auto",
  ].join(";");

  const title = document.createElement("div");
  title.textContent = "KV debug (?kvdebug=1)";
  title.style.cssText = "font-weight:700;margin-bottom:6px;";

  statusEl = document.createElement("div");
  statusEl.style.cssText =
    "margin-bottom:8px;word-break:break-all;opacity:.9;max-height:4.5em;overflow:auto;";

  const row = document.createElement("div");
  row.style.cssText = "display:flex;flex-wrap:wrap;gap:6px;";

  const mkBtn = (label, onClick) => {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.textContent = label;
    btn.style.cssText = [
      "appearance:none",
      "border:1px solid #004d8a",
      "border-radius:6px",
      "background:#004d8a",
      "color:#fff",
      "padding:6px 10px",
      "font:700 11px/1.2 inherit",
      "cursor:pointer",
    ].join(";");
    btn.addEventListener("click", onClick);
    return btn;
  };

  const dumpBtn = mkBtn("Dump", () => {
    pushSnapshot("manual");
  });

  const copyBtn = mkBtn("Copy for AI", async () => {
    pushSnapshot("copy");
    const ok = await copyText(formatForAi());
    copyBtn.textContent = ok ? "Copied!" : "Select & copy";
    window.setTimeout(() => {
      copyBtn.textContent = "Copy for AI";
    }, 1600);
  });

  row.append(dumpBtn, copyBtn);
  panel.append(title, statusEl, row);
  document.body.append(panel);
  return panel;
};

export const initKvDebug = () => {
  const params = new URLSearchParams(location.search);
  if (params.get("kvdebug") !== "1") return () => {};

  document.documentElement.classList.add("is-kv-debug");
  ensurePanel();
  pushSnapshot("init");

  const onResize = () => pushSnapshot("vv-resize");
  window.addEventListener("resize", onResize, { passive: true });
  window.visualViewport?.addEventListener("resize", onResize, { passive: true });

  if (document.readyState === "complete") {
    pushSnapshot("load");
  } else {
    window.addEventListener(
      "load",
      () => {
        pushSnapshot("load");
      },
      { once: true },
    );
  }

  return () => {
    window.removeEventListener("resize", onResize);
    window.visualViewport?.removeEventListener("resize", onResize);
  };
};

export const kvDebugMark = (label) => {
  if (!document.documentElement.classList.contains("is-kv-debug")) return;
  ensurePanel();
  pushSnapshot(label);
};
